import { RegistrationValidationStatus } from '@prisma/client';
import { z } from 'zod';

export const createPastOrPresentDateSchema = (required = false) => {
  const baseSchema = z.string().trim();

  const requiredSchema = required
    ? baseSchema.min(1, { message: 'This date is required' })
    : baseSchema.optional().nullable();

  return requiredSchema.refine(
    (date) => {
      if (!date && !required) return true;

      const inputDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return inputDate <= today;
    },
    { message: 'Date cannot be in the future' }
  );
};

export const createDateSchema = (required = false) => {
  const baseSchema = z.string().trim();

  return required
    ? baseSchema.min(1, { message: 'This date is required' })
    : baseSchema.optional().nullable();
};

const createRegistrationDocumentSchema = (required = false) => {
  const fileNameSchema = z.string().trim();
  const fileUrlSchema = z.string().trim();

  if (required) {
    return z.object({
      fileName: fileNameSchema.min(1, {
        message: 'Registration document is required',
      }),
      fileUrl: fileUrlSchema.min(1, {
        message: 'Registration document is required',
      }),
    });
  }

  return z
    .object({
      fileName: fileNameSchema.optional(),
      fileUrl: fileUrlSchema.optional(),
    })
    .optional();
};

const requiredStringSchema = (fieldName: string) =>
  z
    .string()
    .trim()
    .min(1, { message: `${fieldName} is required` });

const optionalStringSchema = () => z.string().trim().optional().nullable();

const statusSchema = z.nativeEnum(RegistrationValidationStatus, {
  required_error: 'Status is required',
});

/**
 * One identifier the open form is asking about: the field it is rendered under, what to call it,
 * and what a value has to match. The pattern comes from content and is absent more often than not.
 */
export type IdentifierField = {
  name: string;
  label: string;
  regex?: string | null;
};

const baseSchema = z
  .object({
    country: requiredStringSchema('Country'),
    registrationName: requiredStringSchema('Registration name'),
    registrationType: requiredStringSchema('Registration type'),
    jurisdiction: optionalStringSchema(),
    effectiveDate: createPastOrPresentDateSchema(true),
    expirationDate: createDateSchema(false),
    // One value per identifier the registration produces, keyed by the field each is rendered
    // under: the stored identifier's id when editing, the produced key when creating.
    registrationNumbers: z.record(z.string(), z.string()).optional(),
    registrationDocument: createRegistrationDocumentSchema(false),
    status: statusSchema,
    linkedCredential: z.string().trim().optional(),
  })
  .refine(
    (data) => {
      if (!data.expirationDate || !data.effectiveDate) return true;

      const effectiveDate = new Date(data.effectiveDate);
      const expirationDate = new Date(data.expirationDate);

      return expirationDate > effectiveDate;
    },
    {
      message: 'Expiration date must be after effective date',
      path: ['expirationDate'],
    }
  );

/**
 * The rules for the registration currently selected.
 *
 * Built per selection rather than declared once, because what a value has to match depends on which
 * identifiers the chosen registration produces. Every value stays optional — a number that has not
 * been issued yet is the ordinary case, and the holder can record it later — so a pattern is only
 * ever asked of a value someone actually typed.
 */
export const buildAddRegistrationFormSchema = (identifiers: IdentifierField[]) =>
  baseSchema.superRefine((data, ctx) => {
    identifiers.forEach((identifier) => {
      const value = data.registrationNumbers?.[identifier.name]?.trim();
      if (!value || !identifier.regex) return;

      let pattern: RegExp;
      try {
        pattern = new RegExp(identifier.regex);
      } catch {
        // Content is free text. An unparseable pattern validates nothing rather than rejecting.
        return;
      }

      if (!pattern.test(value))
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['registrationNumbers', identifier.name],
          message: `Enter a valid ${identifier.label.toLowerCase()}`,
        });
    });
  });

export const addRegistrationFormSchema = buildAddRegistrationFormSchema([]);

export type AddRegistrationFormSchemaFields = z.infer<typeof baseSchema>;

export const addFQRegistrationFormSchema = z.object({
  registrationName: z.string(),
  jurisdiction: requiredStringSchema('Jurisdiction'),
  effectiveDate: createPastOrPresentDateSchema(true),
  status: statusSchema,
  linkedCredential: z.string().trim().optional(),
  registrationNumber: requiredStringSchema('Registration number'),
  registrationDocument: createRegistrationDocumentSchema(true),
});

export type AddFQRegistrationFormSchemaFields = z.infer<
  typeof addFQRegistrationFormSchema
>;
